// src/pages/RedeprogAdminPage.js
//
// Fase 6 Redeprog SaaS: pantalla del SuperAdmin de Redeprog.
//
// Solo accesible cuando ModoOperacion=Redeprog Y el usuario logueado es
// SuperAdmin de Redeprog (rol Administrador global sin organizacionId).
// El guard SuperAdminRedeprogRoute redirige a otros usuarios.
//
// Permite:
//   - Listar todas las Organizaciones registradas (activas e inactivas).
//   - Crear una Organizacion nueva con branding y config Wompi.
//   - Editar cualquier campo de una Organizacion existente.
//   - Activar / desactivar (bloquea el acceso de un cliente sin borrar sus datos).
//
// La private key de Wompi NUNCA se ingresa aquí — se sube fuera de banda a
// GCP Secret Manager. Este UI solo captura el ID lógico del secreto.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Container, Typography, Button, IconButton, Chip, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Tooltip, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { organizacionesAdminService } from '../services/api';

const EMPTY_FORM = {
  nombre: '',
  slug: '',
  descripcion: '',
  logoUrl: '',
  colorPrimario: '',
  colorSecundario: '',
  tipografia: '',
  dominioPropio: '',
  wompiPublicKey: '',
  wompiAmbiente: '',
  wompiPrivateKeySecretId: '',
};

const SLUG_REGEX = /^[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])?$/;

function OrganizacionDialog({ open, initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_FORM, ...initial } : EMPTY_FORM);
    setError(null);
  }, [initial, open]);

  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setError(null);

    // Validaciones mínimas — el backend valida en serio.
    if (!form.nombre?.trim()) return setError('El nombre es obligatorio.');
    if (!form.slug?.trim()) return setError('El slug es obligatorio.');
    if (!SLUG_REGEX.test(form.slug))
      return setError('El slug debe ser kebab-case: minúsculas, dígitos y guiones, 3-40 caracteres, sin guiones al inicio/fin.');
    if (form.wompiAmbiente && !['test', 'prod'].includes(form.wompiAmbiente))
      return setError('El ambiente Wompi debe ser "test" o "prod".');

    setSaving(true);
    try {
      // Enviamos solo los campos con valor real — el backend hace patch semantics.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v != null)
      );
      if (editing) {
        await organizacionesAdminService.update(initial.id, payload);
      } else {
        await organizacionesAdminService.create(payload);
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Editar organización — ${initial.nombre}` : 'Nueva organización'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Nombre" value={form.nombre}
              onChange={handleChange('nombre')} placeholder="Colegio San José" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Slug (kebab-case)" value={form.slug}
              onChange={handleChange('slug')} placeholder="colegio-san-jose"
              helperText="Se usa como subdominio: <slug>.educat.redeprog.com" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Descripción" value={form.descripcion}
              onChange={handleChange('descripcion')} />
          </Grid>

          <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1 }}>Branding</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Logo URL" value={form.logoUrl}
              onChange={handleChange('logoUrl')} placeholder="https://..." />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Dominio propio (opcional)" value={form.dominioPropio}
              onChange={handleChange('dominioPropio')} placeholder="aula.colegiosanjose.edu.co" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Color primario" value={form.colorPrimario}
              onChange={handleChange('colorPrimario')} placeholder="#2e7d32" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Color secundario" value={form.colorSecundario}
              onChange={handleChange('colorSecundario')} placeholder="#f9a825" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Tipografía" value={form.tipografia}
              onChange={handleChange('tipografia')} placeholder="Inter, sans-serif" />
          </Grid>

          <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1 }}>Pagos Wompi</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Wompi Public Key" value={form.wompiPublicKey}
              onChange={handleChange('wompiPublicKey')} placeholder="pub_prod_..." />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth select label="Ambiente" value={form.wompiAmbiente}
              onChange={handleChange('wompiAmbiente')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="test">test</MenuItem>
              <MenuItem value="prod">prod</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Secret ID (GCP)" value={form.wompiPrivateKeySecretId}
              onChange={handleChange('wompiPrivateKeySecretId')}
              placeholder="wompi-private-key-slug"
              helperText="ID lógico en GCP Secret Manager; el valor real se sube fuera de banda." />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : (editing ? 'Guardar cambios' : 'Crear')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RedeprogAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await organizacionesAdminService.getAll(false);
      setItems(data || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el listado.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleNew = () => { setEditing(null); setDialogOpen(true); };
  const handleEdit = (org) => { setEditing(org); setDialogOpen(true); };

  const handleToggle = async (org) => {
    try {
      await organizacionesAdminService.toggleActiva(org.id);
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar el estado.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Organizaciones</Typography>
          <Typography variant="body2" color="text.secondary">
            Clientes de la plataforma. Cada uno vive en su propio subdominio con su branding y sus datos aislados.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nueva organización
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Dominio propio</TableCell>
              <TableCell align="center">Wompi</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Aún no hay organizaciones registradas.
              </TableCell></TableRow>
            )}
            {items.map((o) => (
              <TableRow key={o.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{o.nombre}</Typography>
                  {o.descripcion && (
                    <Typography variant="caption" color="text.secondary">{o.descripcion}</Typography>
                  )}
                </TableCell>
                <TableCell><code>{o.slug}</code></TableCell>
                <TableCell>{o.dominioPropio || '—'}</TableCell>
                <TableCell align="center">
                  {o.tieneWompiConfigurado
                    ? <Chip size="small" label={o.wompiAmbiente || 'ok'} color="success" />
                    : <Chip size="small" label="sin configurar" variant="outlined" />}
                </TableCell>
                <TableCell align="center">
                  {o.activa
                    ? <Chip size="small" label="activa" color="success" />
                    : <Chip size="small" label="inactiva" color="default" />}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(o)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={o.activa ? 'Desactivar' : 'Activar'}>
                    <IconButton size="small" onClick={() => handleToggle(o)}>
                      {o.activa ? <ToggleOnIcon fontSize="small" color="success" /> : <ToggleOffIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <OrganizacionDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); cargar(); }}
      />
    </Container>
  );
}
